<template>
  <div class="ai-admin-page">
    <header class="ai-page-header">
      <div>
        <h1>只读工具查询</h1>
        <p>
          控制 AI
          可以调用的商城查询能力。所有工具都由后端执行固定查询并校验用户身份，模型无法连接数据库，也不能生成或执行任意
          SQL。
        </p>
      </div>
      <el-button type="primary" :loading="saving" @click="save">保存工具权限</el-button>
    </header>

    <div class="ai-note tools-security-note">
      <i class="el-icon-lock" /><span
        >订单、物流、售后和积分工具只接受后端注入的当前会话用户身份。手机号、地址等敏感字段在返回模型前脱敏。</span
      >
    </div>

    <section class="ai-surface tools-surface">
      <div class="ai-toolbar">
        <div class="ai-toolbar__filters">
          <el-input
            v-model.trim="keyword"
            clearable
            prefix-icon="el-icon-search"
            placeholder="搜索工具名称或说明"
            style="width: 280px"
          />
          <el-select v-model="scope" clearable placeholder="全部数据范围" style="width: 180px">
            <el-option label="公开商品数据" value="公开商品数据" />
            <el-option label="当前会话用户" value="仅当前会话用户" />
            <el-option label="已发布知识" value="已发布知识" />
          </el-select>
        </div>
        <span class="ai-status-line"
          ><span class="ai-status-dot is-ready" />已启用 {{ enabledCount }} /
          {{ tools.length }}</span
        >
      </div>

      <el-table
        v-loading="loading"
        :data="filteredTools"
        style="width: 100%"
        empty-text="未找到匹配的只读工具"
      >
        <el-table-column label="启用" width="90">
          <template slot-scope="scope"><el-switch v-model="scope.row.enabled"/></template>
        </el-table-column>
        <el-table-column label="工具" min-width="180">
          <template slot-scope="scope">
            <strong class="tool-name">{{ scope.row.name }}</strong>
            <code class="tool-code">{{ scope.row.code }}</code>
          </template>
        </el-table-column>
        <el-table-column prop="description" label="用途" min-width="260" />
        <el-table-column label="数据范围" min-width="160">
          <template slot-scope="scope"
            ><el-tag size="small" effect="plain">{{ scope.row.dataScope }}</el-tag></template
          >
        </el-table-column>
        <el-table-column prop="output" label="返回内容" min-width="180" />
        <el-table-column label="权限" width="100">
          <template
            ><span class="readonly-badge"><i class="el-icon-view" />只读</span></template
          >
        </el-table-column>
      </el-table>
    </section>

    <section class="tool-boundary">
      <h2>工具执行边界</h2>
      <dl>
        <div>
          <dt>身份来源</dt>
          <dd>登录态与客服会话，模型参数不能覆盖用户 ID</dd>
        </div>
        <div>
          <dt>查询方式</dt>
          <dd>复用商城 Service/DAO 的参数化查询，不接收 SQL</dd>
        </div>
        <div>
          <dt>禁止操作</dt>
          <dd>退款、改价、取消订单、修改地址、余额与支付写操作</dd>
        </div>
        <div>
          <dt>审计记录</dt>
          <dd>记录会话、工具、参数摘要、结果状态与耗时</dd>
        </div>
      </dl>
    </section>
  </div>
</template>

<script>
import { getKefuAiTools, saveKefuAiTools } from "@/api/kefu";

export default {
  name: "KefuReadonlyTools",
  data() {
    return { loading: false, saving: false, keyword: "", scope: "", tools: [] };
  },
  computed: {
    enabledCount() {
      return this.tools.filter(item => item.enabled).length;
    },
    filteredTools() {
      const keyword = this.keyword.toLowerCase();
      return this.tools.filter(item => {
        const text = `${item.name} ${item.code} ${item.description}`.toLowerCase();
        return (
          (!keyword || text.includes(keyword)) && (!this.scope || item.dataScope === this.scope)
        );
      });
    }
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        this.tools = await getKefuAiTools();
      } finally {
        this.loading = false;
      }
    },
    async save() {
      this.saving = true;
      try {
        const enabled = this.tools.filter(item => item.enabled).map(item => item.code);
        this.tools = await saveKefuAiTools(enabled);
        this.$message.success("只读工具权限已保存");
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style lang="scss">
@import "./ai-management.scss";
.tools-security-note {
  margin-bottom: 18px;
}
.tools-surface {
  overflow: hidden;
}
.tool-name {
  display: block;
  margin-bottom: 6px;
  color: #202332;
}
.tool-code {
  color: #656b7b;
  font-family: Consolas, monospace;
  font-size: 12px;
}
.readonly-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #168559;
  font-size: 13px;
}
.tool-boundary {
  max-width: 1080px;
  margin-top: 18px;
  padding: 20px 22px;
  border: 1px solid #e5e7ef;
  border-radius: 12px;
  background: #fff;
}
.tool-boundary h2 {
  margin: 0 0 16px;
  font-size: 16px;
  color: #202332;
}
.tool-boundary dl {
  display: grid;
  grid-template-columns: repeat(2, minmax(260px, 1fr));
  gap: 0;
  margin: 0;
}
.tool-boundary dl div {
  display: grid;
  grid-template-columns: 90px 1fr;
  gap: 10px;
  padding: 12px 0;
  border-top: 1px solid #eceef4;
}
.tool-boundary dl div:nth-child(odd) {
  padding-right: 28px;
}
.tool-boundary dt {
  color: #4c5264;
  font-weight: 600;
}
.tool-boundary dd {
  margin: 0;
  color: #6a7082;
  line-height: 1.55;
}
@media (max-width: 760px) {
  .tool-boundary dl {
    grid-template-columns: 1fr;
  }
  .tool-boundary dl div:nth-child(odd) {
    padding-right: 0;
  }
}
</style>
